/* eslint-disable import/prefer-default-export */
import { isNil, forEach, includes, join } from 'lodash';
import { objtype } from '@/constants/objtype';
import { objCompanyDefine } from '@/constants/company';
import { isNumber } from '@/utils/number';
import { isTrue, booleanDefine } from '@/utils/boolean';
import { getValueAfterCheckEmpty, EMPTY_TEXT } from '@/utils/string';
import { getDuration } from '@/utils/date';

/**
 * 네트워크 또는 무선 네트워크 장비의 objtype 변경 시 objDefine 도 함께 변경해야 한다면
 * objDefineId 를 추가합니다.
 * @author Hunui
 * @param  {[type]} obj [description]
 * @return {[type]}     [description]
 */
export function checkNetdeviceObjDefine(obj) {
  const objtypeId = obj.objtypeId;
  if (isNil(objtypeId)) return obj;

  if (objtypeId === objtype.WIRELESS_AP || objtypeId === objtype.AP_CONTROLLER) {
    obj.objDefineId = 'WIRELESS_DEVICE';
  } else {
    obj.objDefineId = 'NETDEVICE';
  }
  return obj;
}
/**
 * 무선 AP의 채널에 따라 대역폭을 반환합니다.
 * @author Hunui
 * @param  {[type]}  channel             [description]
 * @param  {Boolean} [includeUnit=false] [description]
 * @return {[type]}                      [description]
 */
export function getRadioBandByChannel(channel, includeUnit = false) {
  const channels = channel.split(',');
  const bands = [];
  forEach(channels, (item) => {
    if (isNumber(item)) {
      const ch = parseInt(item, 10);
      let band;
      if (ch >= 1 && ch <= 14) {
        band = '2.4';
      } else {
        band = '5';
      }
      band = includeUnit ? `${band} GHz` : band;
      if (!includes(bands, band)) {
        bands.push(band);
      }
    }
  });
  return join(bands, ', ');
}

/**
 * RSSI에 맞는 신호세기 아이콘 클래스를 반환한다.
 * @author Ju
 * @param  {Number} rssi RSSI
 * @return {String}      아이콘 클래스
 */
export function getSignalStrengthIconClass(rssi) {
  let iconClass = 'wireless-signal-icon strength-';
  if (!isNil(rssi) && isNumber(rssi)) {
    if (rssi < -70) {
      iconClass += '1';
    } else if (rssi <= -60) {
      iconClass += '2';
    } else if (rssi <= -50) {
      iconClass += '3';
    } else {
      iconClass += '4';
    }
  } else {
    iconClass += '0';
  }
  return iconClass;
}

/**
 * 접속자 명이 빈 값 또는 N/A이면  IP주소를 반환한다.
 * @author jeee12
 * @param  {[type]} clientName 접속자명
 * @param  {[type]} ipaddress  IP주소
 * @return {[type]}            [description]
 */
export function getClientName(clientName, ipaddress) {
  if (isNil(clientName) || clientName === '' || clientName === 'N/A') {
    return getValueAfterCheckEmpty(ipaddress);
  }
  return clientName;
}

/**
 * 접속자 접속 유지 시간을 문자열로 치환하여 반환한다.
 * @author Ju
 * @param  {Number} duration 접속유지시간 (초)
 * @return {String}
 */
export function getClientConnectDurationString(duration) {
  return isNil(duration) ? EMPTY_TEXT : getDuration(duration * 1000);
}

/**
 * 벤더 기준으로 AP 동작 상태에 대한 이름을 반환한다.
 * @author Ju
 * @param  [Number, Boolean, String] status
 * @return {[type]}        [description]
 */
export function getApStatusName(status) {
  if (isNil(status)) {
    return EMPTY_TEXT;
  }
  return isTrue(status) ? 'UP' : 'DOWN';
}

/**
 * AP의 SNR 정보 제공 여부를 반환한다.
 * @author Ju
 * @param  {Number} objCompanyId 벤더 아이디
 * @return {Boolean}              true: 제공 / false: 미제공
 */
export function showApSnr(objCompanyId) {
  return objCompanyDefine.CISCO !== objCompanyId && objCompanyDefine.XIRRUS !== objCompanyId;
}

/**
 * 비인가 AP 상태 정보 제공 여부를 반환한다.
 * @author Ju
 * @param  {Number} objCompanyId 벤더 아이디
 * @return {Boolean}              true: 제공 / false: 미제공
 */
export function showRogueApStatus(objCompanyId) {
  return objCompanyDefine.CISCO !== objCompanyId;
}

/**
 * 접속자의 RSSI 정보 제공 여부를 반환한다. (NOTE: 20.03.05 Ju 기준 현재 목표 벤더 모두 지원. 추후 제거 필요.)
 * @author Ju
 * @param  {Number} objCompanyId 벤더 아이디
 * @return {Boolean}              true: 제공 / false: 미제공
 */
export function showClientRssi() {
  return true;
}

/**
 * 접속자의 SNR 정보 제공 여부를 반환한다. (NOTE: 20.03.05 Ju 기준 현재 목표 벤더 모두 지원. 추후 제거 필요.)
 * @author Ju
 * @param  {Number} objCompanyId 벤더 아이디
 * @return {Boolean}              true: 제공 / false: 미제공
 */
export function showClientSnr(objCompanyId) {
  return objCompanyDefine.XIRRUS !== objCompanyId;
}

/**
 * 접속자의 접속 유지 시간 정보 제공 여부를 반환한다.
 * @author Ju
 * @param  {Number} objCompanyId 벤더 아이디
 * @return {Boolean}              true: 제공 / false: 미제공
 */
export function showClientConnectDuration(objCompanyId) {
  return objCompanyDefine.CISCO !== objCompanyId;
}

/**
 * 비인가 AP 상태 값을 적절한 값으로 치환하여 반환한다.
 * @author Ju
 * @param  {[type]} value [description]
 * @return {[type]}       [description]
 */
export function getRogueApStatus(value) {
  if (isNil(value) || value === '') {
    return EMPTY_TEXT;
  }
  const dataType = typeof value;
  if (dataType === 'boolean') {
    return value ? 'UP' : 'DOWN';
  } else if (dataType === 'number') {
    return booleanDefine.TRUE_1 === value;
  }
  const valueStr = value.toUpperCase();
  if (booleanDefine.TRUE_STR_1 === valueStr || booleanDefine.TRUE_Y.toUpperCase() === valueStr || booleanDefine.TRUE_STR.toUpperCase() === valueStr) {
    return 'UP';
  } else if (booleanDefine.FALSE_STR_0 === valueStr || booleanDefine.FALSE_N.toUpperCase() === valueStr || booleanDefine.FALSE_STR.toUpperCase() === valueStr) {
    return 'DOWN';
  }
  return value;
}



// WEBPACK FOOTER //
// ./src/utils/wireless-device.js